/**
 * The globe, plus every imperative command the match screen wants to give it.
 *
 * INVARIANT 7 — nothing here asks for labels, and there is no prop on
 * `GlobeProps` that could turn them on. The globe stays a shape, not a map.
 *
 * The commands are wrapped: `@geogame/globe` is developed alongside this
 * package and its handle throws while a method is still landing. A globe that
 * cannot fly somewhere must not take the match down with it — the pick handler
 * is the only part of this integration that gameplay actually depends on.
 *
 * ===========================================================================
 * WHAT THIS FILE IS ALLOWED TO KNOW (invariant 5)
 * ===========================================================================
 *
 * Two different kinds of thing arrive here and they must not be confused.
 *
 *   `pickFeedback` is the player's OWN tap, graded. It names only a country the
 *   player themselves chose, so it is safe at any time — including while the
 *   pinpoint rung is still live and they have retries left. This is change 2.
 *
 *   `reveal` names the TARGET. It exists only for a rung that is over —
 *   `AnswerReveal` cannot be minted otherwise — so by the time this component
 *   can see one there is nothing left to protect. This is change 3.
 *
 * A wrong pick with attempts remaining therefore gets a red ring on the
 * player's own country and NOTHING ELSE: no camera move, no second ring, no
 * hint of where the right answer is.
 */

import { useEffect, useRef, useState } from 'react';
import { breadcrumb } from '../lib/telemetry';
import { Globe } from '@geogame/globe';
import type { GlobeHandle, GlobePick, RegionCollection } from '@geogame/globe';
import type { AnswerReveal, LngLat } from '@geogame/shared';

import type { PickFeedback } from '../store/gameStore';

function safely(fn: () => void): void {
  try {
    fn();
  } catch {
    // The globe reports unimplemented commands by throwing. Rendering and
    // picking still work, so a missing camera move is cosmetic.
  }
}

/** Ask the globe whether it is suppressing motion; assume not if it cannot say. */
function reducedMotion(g: GlobeHandle): boolean {
  try {
    return g.prefersReducedMotion();
  } catch {
    return false;
  }
}

/**
 * How long a red ring stays on a country the player may still re-pick. Long
 * enough to register as a verdict, short enough to be gone before a quick
 * player's next tap lands on top of it.
 */
const RETRY_MARK_MS = 1_300;

/**
 * The reveal's beats, in milliseconds from the reveal arriving.
 *
 * The brief names the old behaviour: "the current reveal is a 900ms
 * flyToCountry with no emphasis. Make it feel like a reveal." A camera move on
 * its own is not an event, because nothing happens AT the destination. So the
 * wrong pick is marked first, the camera then travels, and the answer's ring
 * lands as the flight settles — three beats in sequence rather than one move.
 */
const REVEAL = {
  /** Red on the player's own pick, immediately: what they did is the premise. */
  markPickedMs: 0,
  /** Then the journey. Slower than the old 900ms — it is meant to be watched. */
  flyAtMs: 260,
  flyDurationMs: 1_150,
  /** The green ring lands as the camera stops, so it arrives to meet you. */
  markCorrectMs: 1_150,
} as const;

export interface GlobeStageProps {
  readonly roundIdx: number | null;
  readonly interactive: boolean;
  readonly snapRadiusPx: number;
  /** Explorer tier pre-orients to the target's region (spec 3). */
  readonly orientRegion: string | null;
  readonly yourPin: LngLat | null;
  /** Draw the eclipse: shadow closed, borders gone. */
  readonly eclipsed?: boolean;
  readonly opponentPin: LngLat | null;
  /**
   * CHANGE 2. The player's own last pinpoint, graded — green if right, red if
   * wrong. Never names the target.
   */
  readonly pickFeedback: PickFeedback | null;
  /**
   * CHANGE 3. The answer to a rung that is OVER. Drives the full reveal for a
   * pinpoint; for a probe rung the card does the talking and the globe only
   * travels to the country the round was about.
   */
  readonly reveal: AnswerReveal | null;
  /** The country the round was about, once the round has ended. */
  readonly revealCca3: string | null;
  readonly revealLngLat: LngLat | null;
  /** City mode pins raw coordinates and reveals a point rather than a country shape. */
  readonly exactTarget?: boolean;
  /**
   * Subdivision outlines for the states/provinces mode, or null in every other
   * mode. Handing them over is what makes a tap resolve to `US-OH` instead of
   * to `USA` — see `GlobeProps.regions`.
   */
  readonly regions?: RegionCollection | null;
  /**
   * Where the camera opens each round, instead of the whole globe.
   *
   * The subdivision mode uses it to frame a country: fifty states asked for one
   * at a time from a view of the entire planet would be fifty rounds spent
   * zooming rather than answering. Null keeps the ordinary home view.
   */
  readonly homeBounds?: readonly [number, number, number, number] | null;
  /**
   * The dots of a live MAP CHOICE rung, in option order — the four cities the
   * player is choosing between. Null whenever no such rung is on screen, and
   * clearing them is as load-bearing as showing them: four cities left on the
   * globe during the next round's pinpoint are four wrong answers.
   */
  readonly choiceMarkers?: readonly LngLat[] | null;
  /**
   * A resolved map choice: which dot was right, and which the player tapped.
   * Post-resolution only — it comes from `AnswerReveal`, which cannot be minted
   * for a rung that is still in play (change 3).
   */
  readonly choiceReveal?: {
    readonly markers: readonly LngLat[];
    readonly correctIndex: number;
    /** Which dot the player tapped, or null if the clock beat them to it. */
    readonly pickedIndex: number | null;
  } | null;
  /**
   * Fired when the gold answer pin lands, and again when a new round wipes it.
   *
   * The pin key that names it is rendered by the MATCH SCREEN, inside the HUD's
   * own flow — see `PinKey`. Only this component knows when the pin is actually
   * on the globe (it lands a beat after the reveal starts, behind the flight
   * timers), so the key cannot be allowed to name it from up there on its own.
   */
  readonly onTargetPinShown?: (shown: boolean) => void;
  readonly onPick: (pick: GlobePick) => void;
}

export function GlobeStage({
  roundIdx,
  interactive,
  snapRadiusPx,
  orientRegion,
  yourPin,
  eclipsed,
  opponentPin,
  pickFeedback,
  reveal,
  revealCca3,
  revealLngLat,
  exactTarget = false,
  regions = null,
  homeBounds = null,
  choiceMarkers = null,
  choiceReveal = null,
  onTargetPinShown,
  onPick,
}: GlobeStageProps): React.JSX.Element {
  const ref = useRef<GlobeHandle | null>(null);
  /*
   * MapLibre emits `load` a second or two after mount, and every camera command
   * before that is silently dropped — `fitBoundsOn` and `cameraTo` both return
   * early without a loaded map. Round 1 therefore arms while the globe is still
   * booting, and in the subdivision mode that meant the FIRST round opened on
   * the default view of Africa with a prompt asking for Ohio.
   *
   * So the opening camera waits for the globe to say it is ready, and re-runs
   * when it does. Rounds 2..N are unaffected: `ready` is already true by then
   * and the effect fires on `roundIdx` exactly as before.
   */
  const [ready, setReady] = useState(false);
  const pickRef = useRef(onPick);
  pickRef.current = onPick;
  // The gold pin lands a beat after the reveal starts; the key waits for it.
  const targetShownRef = useRef(onTargetPinShown);
  targetShownRef.current = onTargetPinShown;
  const reportTargetPin = (shown: boolean): void => targetShownRef.current?.(shown);

  // A new round wipes the board.
  useEffect(() => {
    const g = ref.current;
    if (!g) return;
    safely(() => g.clearPins());
    safely(() => g.clearMarks());
    safely(() => g.clearHighlights());
    reportTargetPin(false);
    if (homeBounds) safely(() => g.fitBounds(homeBounds, { durationMs: 600, padding: 28 }));
    else safely(() => g.resetView(600));
    if (orientRegion) safely(() => g.orientToRegion(orientRegion, 700));
  }, [ready, roundIdx, orientRegion, homeBounds]);

  useEffect(() => {
    const g = ref.current;
    if (!g) return;
    safely(() => g.setInteractive(interactive));
  }, [interactive]);

  useEffect(() => {
    const g = ref.current;
    if (!g || !yourPin) return;
    safely(() => g.dropPin('you', yourPin, 'you'));
  }, [yourPin]);

  /*
   * THE OTHER SEAT'S PIN, ONLY WHEN THERE IS SOMETHING TO COMPARE IT TO.
   *
   * A round the player let time out used to end with two dots on the globe and
   * neither of them theirs: the answer in gold and the opponent's guess in
   * orange. Two unexplained markers where the player made no mark read as two
   * candidate answers, and the one that is actually the answer is not the one
   * the eye goes to first.
   *
   * The opponent's pin only ever meant anything as a COMPARISON — theirs
   * against yours against the truth. With no pin of your own there is no
   * comparison, so it is not shown. The gold pin then stands alone and means
   * exactly one thing.
   */
  useEffect(() => {
    const g = ref.current;
    if (!g) return;
    if (!opponentPin || !yourPin) return;
    safely(() => g.dropPin('opponent', opponentPin, 'opponent'));
  }, [opponentPin, yourPin]);

  /*
   * THE MAP CHOICE'S DOTS.
   *
   * Keyed on the array so that leaving the rung — by answering it, by busting,
   * or by the round ending — takes them off the globe again. They are drawn as
   * their own pin kind rather than as `target` pins: a gold target pin means
   * "this is the answer", and at the moment these are on screen exactly one of
   * them is and the globe is not allowed to say which.
   */
  useEffect(() => {
    const g = ref.current;
    if (!g) return undefined;
    if (!choiceMarkers || choiceMarkers.length === 0) return undefined;
    choiceMarkers.forEach((lngLat, i) => {
      safely(() => g.dropPin(`choice:${i}`, lngLat, 'choice'));
    });
    /*
     * The rung-1 pin comes OFF for the duration.
     *
     * It is a fifth dot inside the same state, a few pixels from a real
     * candidate — Columbus sits almost exactly where a player aiming at the
     * middle of Ohio put theirs — and on the one rung that is answered by
     * aiming, an extra dot is not a record of what you did, it is a decoy. It
     * goes back on when the rung ends.
     */
    safely(() => g.removePin('you'));

    /*
     * FLY TO THE DOTS. Not decoration — the rung is unanswerable without it.
     *
     * The round opens framed on a whole country, and at that framing Rhode
     * Island's four cities are inside three pixels of each other. `maxZoom`
     * stops a small state from becoming a street map, and the margin keeps the
     * outermost dots off the edge of the canvas where the HUD covers them.
     */
    const lngs = choiceMarkers.map((m) => m[0]);
    const lats = choiceMarkers.map((m) => m[1]);
    const margin = 0.35;
    safely(() =>
      g.fitBounds(
        [
          Math.min(...lngs) - margin,
          Math.min(...lats) - margin,
          Math.max(...lngs) + margin,
          Math.max(...lats) + margin,
        ],
        { padding: 72, durationMs: 700, maxZoom: 5.4 },
      ),
    );
    return () => {
      // One by one, not `clearPins`: the answer pin and the opponent's are on
      // the same source and this rung has nothing to say about either.
      choiceMarkers.forEach((_, i) => safely(() => g.removePin(`choice:${i}`)));
      if (yourPin) safely(() => g.dropPin('you', yourPin, 'you'));
    };
  }, [choiceMarkers, yourPin]);

  /*
   * The eclipse falls, or lifts.
   *
   * Driven off the ROUND's flag rather than a one-shot event, so a reconnect
   * mid-eclipse redraws it: `round.arm` is replayed on resume and carries the
   * same per-seat flag, which means a player who dropped out and came back
   * finds the world exactly as dark as they left it.
   *
   * A TOP-LEVEL HOOK, and it has to be. This effect spent its first outing
   * nested inside the map-choice effect above, where it did nothing at all in
   * country and city mode: that effect returns early when there are no choice
   * markers, so the line calling `setEclipse` was never reached and the globe
   * kept its borders through every eclipse the server ever declared. The
   * banner said the shadow had fallen and the world disagreed.
   *
   * In the one mode that DOES set markers it was worse than useless, because
   * `useEffect` called from inside a running effect is an invalid hook call
   * and throws.
   */
  useEffect(() => {
    const g = ref.current;
    if (!g || !ready) return;
    safely(() => g.setEclipse(eclipsed === true));
  }, [ready, eclipsed]);

  /*
   * THE MAP CHOICE, REVEALED. The right city in gold, the tapped one in the
   * player's own blue — the same two colours the pinpoint reveal uses, meaning
   * the same two things.
   */
  useEffect(() => {
    const g = ref.current;
    if (!g || !choiceReveal) return;
    // The right city, in the gold that means "answer" everywhere else.
    const correct = choiceReveal.markers[choiceReveal.correctIndex];
    if (correct) safely(() => g.dropPin('choice-answer', correct, 'target'));

    /*
     * AND WHERE THEY ACTUALLY TAPPED, in their own blue.
     *
     * Only the gold pin used to land, on the reasoning that naming the tapped
     * city on the reveal card was enough and a second blue dot would compete
     * with the rung-1 state pin that comes back when the rung ends. The first
     * half was wrong: a lone gold dot says "the capital is here" and answers
     * nothing about the guess it is correcting, so a player who missed by one
     * city and a player who missed by four hundred miles see the same picture.
     * Reported as the answer not being made clear enough.
     *
     * The second half is handled rather than accepted. This reuses the `you`
     * id, so it REPLACES the state pin instead of competing with it: for the
     * length of the reveal the globe holds exactly two dots, the answer and
     * the guess, which is the language the pinpoint reveal already speaks.
     */
    const picked =
      choiceReveal.pickedIndex === null ? null : choiceReveal.markers[choiceReveal.pickedIndex];
    if (picked && choiceReveal.pickedIndex !== choiceReveal.correctIndex) {
      safely(() => g.dropPin('you', picked, 'you'));
    }
    // Depends on the VALUES rather than on the object: the match screen builds
    // that object inline on every render, and it cannot memoise it — there is
    // an early return above the point it is computed.
  }, [choiceReveal?.markers, choiceReveal?.correctIndex, choiceReveal?.pickedIndex]);

  /*
   * CHANGE 2 — IMMEDIATE PINPOINT FEEDBACK.
   *
   * Before this, the only `highlight()` call in the app was gated on the
   * round-end reveal, so a pinpoint produced a pin and then absolute silence. A
   * player who pushed never found out whether the thing they were betting on
   * was even right.
   *
   * `markCountry` rather than `highlight` because ~30 pool members have no
   * polygon and render as a 2.6px dot: recolouring their fill is feedback
   * nobody can see, and it would be missing precisely where the pinpoint was
   * hardest. The mark is a screen-space ring, so Nauru and Brazil get the same
   * verdict at the same size.
   *
   * Keyed on `seq`, not on the country code — two wrong taps on the same
   * country have to flash twice.
   */
  useEffect(() => {
    const g = ref.current;
    if (!g || !pickFeedback || exactTarget) return undefined;
    const code = pickFeedback.cca3;
    // An ocean tap has no country to mark. The prompt card says so in words.
    if (!code) return undefined;

    safely(() => g.markCountry(code, pickFeedback.correct ? 'correct' : 'incorrect'));

    // A miss that can still be retried must not leave its red ring lying on the
    // globe while the player lines up the next tap.
    if (!pickFeedback.correct && pickFeedback.retryable) {
      const t = window.setTimeout(() => safely(() => g.unmarkCountry(code)), RETRY_MARK_MS);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [exactTarget, pickFeedback]);

  /*
   * CHANGE 3 — THE REVEAL.
   *
   * Sequenced rather than fired at once, and the sequence is the point: mark
   * what they did, travel, then land the answer. Under reduced motion the whole
   * thing collapses to both marks and a jump — every beat still happens, none
   * of them moves.
   */
  useEffect(() => {
    const g = ref.current;
    if (!g || (!revealCca3 && !revealLngLat)) return undefined;

    const timers: number[] = [];
    const at = (ms: number, fn: () => void): void => {
      if (ms <= 0) {
        fn();
        return;
      }
      timers.push(window.setTimeout(fn, ms));
    };

    const still = reducedMotion(g);
    const pickedWrong =
      reveal?.kind === 'pinpoint' && reveal.pickedCca3 && reveal.pickedCca3 !== revealCca3
        ? reveal.pickedCca3
        : null;

    if (pickedWrong) {
      at(REVEAL.markPickedMs, () => safely(() => g.markCountry(pickedWrong, 'incorrect')));
    }
    at(still ? 0 : REVEAL.flyAtMs, () => {
      if (exactTarget && revealLngLat) {
        safely(() =>
          g.flyToLngLat(revealLngLat, { zoom: 3.2, durationMs: still ? 0 : REVEAL.flyDurationMs }),
        );
      } else if (revealCca3) {
        safely(() =>
          g.flyToCountry(revealCca3, { durationMs: still ? 0 : REVEAL.flyDurationMs }),
        );
      }
    });
    at(still ? 0 : REVEAL.markCorrectMs, () => {
      if (!exactTarget && revealCca3) safely(() => g.markCountry(revealCca3, 'correct'));
      if (revealLngLat) {
        safely(() => g.dropPin('target', revealLngLat, 'target'));
        reportTargetPin(true);
      }
    });

    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [exactTarget, reveal, revealCca3, revealLngLat]);

  return (
    <div className="gg-globe-stage">
      <Globe
        ref={ref}
        className="gg-globe"
        regions={regions}
        snapRadiusPx={exactTarget ? 0 : snapRadiusPx}
        disambiguate={!exactTarget}
        interactive={interactive}
        /*
         * The idle spin is for a globe with nowhere in particular to be. A mode
         * that opens framed on one country has somewhere: left spinning, the
         * camera walks off the United States during the decision window and the
         * next rung has to fly it back.
         */
        idleSpin={!interactive && !homeBounds}
        onReady={() => setReady(true)}
        onPick={(pick) => {
          /*
           * THE BREADCRUMB THAT MATTERS FOR A GLOBE FREEZE.
           *
           * A tap is where the expensive work happens — hit-testing, the
           * outward ring search, the disambiguation camera — and it is the
           * last thing that ran before the report gets written. Recording what
           * the tap RESOLVED TO is the difference between "it hung" and "it
           * hung resolving CHE against LIE", which is the difference between
           * a guess and a fix.
           */
          breadcrumb(`pick ${pick.cca3 ?? 'ocean'}`);
          pickRef.current(pick);
        }}
      />
    </div>
  );
}
